import { useMemo, useRef } from "react";

import useUserUnit from "hooks/useUserUnit";

import { INormalizeBySetting } from "models";

import { useEvaUnitCore } from "./useEvaUnitCore";

/**
 * Hook to correct the unit in normalization settings based on the user's unit system.
 *
 * For built-in fields with a `units` dictionary (e.g., { 0: "m", 1: "m", 2: "ft" }),
 * it uses the dictionary to get the correct unit for the user's unit system.
 *
 * For custom fields (org-defined/user-defined) that have `defaultUnit` but no `units` mapping,
 * it uses eva-unit-core to convert the defaultUnit to the appropriate unit for the user's system.
 */
export function useNormalizationUnitCorrection<
  T extends Maybe<Partial<INormalizeBySetting>>
>(normalizeBySettings: T): T {
  const unitType = useUserUnit();
  const { isReady, getTargetUnit } = useEvaUnitCore();

  // Use ref to store getTargetUnit to avoid infinite loops
  // (the function reference changes on every render)
  const getTargetUnitRef = useRef(getTargetUnit);
  getTargetUnitRef.current = getTargetUnit;

  return useMemo(() => {
    if (!normalizeBySettings) return normalizeBySettings;

    const units = normalizeBySettings.units;
    const defaultUnit = normalizeBySettings.defaultUnit;

    // Case 1: Has units dictionary - use it directly
    if (units && units[unitType] && normalizeBySettings.unit !== units[unitType]) {
      return {
        ...normalizeBySettings,
        unit: units[unitType]
      };
    }

    // Case 2: Has defaultUnit but no units mapping - use eva-unit-core to convert
    // This handles custom fields (org-defined/user-defined columns)
    if (
      isReady &&
      defaultUnit &&
      (!units || !units[unitType]) &&
      normalizeBySettings.unit !== undefined
    ) {
      const convertedUnit = getTargetUnitRef.current(defaultUnit, unitType);

      // Only update if conversion produced a different unit
      if (convertedUnit && convertedUnit !== normalizeBySettings.unit) {
        return {
          ...normalizeBySettings,
          unit: convertedUnit
        };
      }
    }

    return normalizeBySettings;
  }, [normalizeBySettings, unitType, isReady]);
}

export default useNormalizationUnitCorrection;
